import { apiClient } from '~/lib/apiClient';
import { findErrorCodeDescription, isArcApiError } from '~/src/services/errors';
import {
  OnErrorCallback,
  OnSuccessCarrierCallback,
  OnSuccessInstantPaymentCallback,
  OnSuccessRedirectCallback,
  PayOrderInput,
  payOrder,
} from './pay';

import type { PaymentMethodVariables } from '~/types/payment';
import type { DeliveryInfo } from '~/types/resources/deliveryInfo';
import type { GachaDetail } from '~/types/resources/gachaDetail';

export type BuyOrderInput = {
  gacha: GachaDetail;
  play_amount: number;
  delivery_info: DeliveryInfo | null;
  user_token: string;
  shouldNotDeliver: boolean;
  onErrorCallback: OnErrorCallback;
  webroll_live_tipping_message?: string;
} & (
  | {
      method: Extract<PaymentMethodVariables, 'play_ticket'>;
      playTicketInput: { id: string };
      onSuccessCallback: OnSuccessInstantPaymentCallback;
    }
  | {
      method: Extract<PaymentMethodVariables, 'credit'>;
      cardSeq: string;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
      onSuccessRedirectCallback: OnSuccessRedirectCallback;
    }
  | {
      method: Extract<PaymentMethodVariables, 'paypay' | 'alipay+'>;
      onSuccessCallback: OnSuccessRedirectCallback;
    }
  | {
      method: Extract<PaymentMethodVariables, 'applepay'>;
      domainName: string;
      label: string;
      applePaySession: ApplePaySession;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
    }
  | {
      method: Extract<PaymentMethodVariables, 'googlepay'>;
      totalPrice: string;
      onSuccessCallback: OnSuccessInstantPaymentCallback;
      onErrorCallback: OnErrorCallback;
    }
  | {
      method: Extract<
        PaymentMethodVariables,
        'carriers:au' | 'carriers:docomo' | 'carriers:softbank' | 'unionpay'
      >;
      onSuccessCallback: OnSuccessCarrierCallback;
    }
);

// 注文の作成と支払いを行う
export const buyOrder = async (input: BuyOrderInput) => {
  const { method } = input;
  try {
    const order = await createOrder({
      gacha: input.gacha,
      playAmount: input.play_amount,
      deliveryInfo: input.delivery_info,
      userToken: input.user_token,
      shouldNotDeliver: input.shouldNotDeliver,
      tippingMessage: input.webroll_live_tipping_message,
    });
    const returnUrl = generateReturnUrl(input.gacha, order);

    const payOrderInput: PayOrderInput = (() => {
      switch (method) {
        case 'credit':
          return {
            method,
            order_id: order,
            cardSeq: input.cardSeq,
            user_token: input.user_token,
            returnUrl: generateCreditReturnUrl(input.gacha, order),
            onSuccessCallback: input.onSuccessCallback,
            onSuccessRedirectCallback: input.onSuccessRedirectCallback,
            onErrorCallback: input.onErrorCallback,
          };
        case 'paypay':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            returnUrl: returnUrl,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
          };
        case 'alipay+':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            returnUrl: returnUrl,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
          };
        case 'applepay':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            returnUrl: returnUrl,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
            domainName: input.domainName,
            label: input.label,
            applePaySession: input.applePaySession,
          };
        case 'googlepay':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            totalPrice: input.totalPrice,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
          };
        case 'play_ticket':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
            playTicketInput: input.playTicketInput,
          };
        case 'carriers:au':
        case 'carriers:docomo':
        case 'carriers:softbank':
        case 'unionpay':
          return {
            method,
            order_id: order,
            user_token: input.user_token,
            onSuccessCallback: input.onSuccessCallback,
            onErrorCallback: input.onErrorCallback,
            returnUrl: returnUrl,
          };
        default:
          throw new Error('invalid payment method');
      }
    })();
    try {
      await payOrder(payOrderInput);
    } catch (e) {
      if (!(e instanceof Error)) {
        input.onErrorCallback('支払いに失敗しました');
        return;
      }
      const desc = findErrorCodeDescription(e.name);
      if (desc != null) {
        input.onErrorCallback(
          '支払いに失敗しました',
          `${desc.title}:\n ${desc.description}`
        );
        return;
      }
      if (isArcApiError(e)) {
        input.onErrorCallback(e.message);
        return;
      }
      input.onErrorCallback('支払いに失敗しました');
      return;
    }
  } catch (e) {
    const errorTitle = '注文を作成できませんでした';
    if (isArcApiError(e)) {
      const arcError = findErrorCodeDescription(e.code);
      input.onErrorCallback(
        arcError?.title ?? errorTitle,
        arcError?.description ?? e.message
      );
      return;
    }
    if (!(e instanceof Error)) {
      input.onErrorCallback(errorTitle, e.message);
      return;
    }
    const desc = findErrorCodeDescription(e.name);
    if (desc != null) {
      input.onErrorCallback(errorTitle, `${desc.title}:\n${desc.description}`);
      return;
    }
    input.onErrorCallback(errorTitle, `${e.message}: ${e.name}`);
    return;
  }
};

// 注文を作成する
const createOrder = async (input: {
  gacha: GachaDetail;
  playAmount: number;
  deliveryInfo: DeliveryInfo | null;
  userToken: string;
  shouldNotDeliver: boolean;
  tippingMessage?: string;
}): Promise<string> => {
  const {
    gacha,
    playAmount,
    deliveryInfo,
    userToken,
    shouldNotDeliver,
    tippingMessage,
  } = input;

  const requestDeliveryInfo = shouldNotDeliver ? null : deliveryInfo;
  const reqBody = {
    headers: {
      Authorization: `Bearer ${userToken}`,
    },
    body: {
      play_amount: playAmount,
      delivery_info: requestDeliveryInfo ?? undefined,
      is_no_require_delivery: shouldNotDeliver,
      webroll_live_tipping_message: tippingMessage ?? undefined,
    },
  };
  const gachaOrder = await apiClient.v1.gachas
    ._gachaId(gacha.id)
    .orders.post(reqBody);
  if (gachaOrder.status != 200) {
    throw gachaOrder.body;
  }
  return gachaOrder.body.id;
};

export const generateReturnUrl = (
  gacha: GachaDetail,
  order_id: string
): string => {
  return `${location.origin}/e/${gacha.unique_name}/orders/${order_id}/redirect`;
};

export const generateCreditReturnUrl = (
  gacha: GachaDetail,
  order_id: string
): string => {
  return `${location.origin}/e/${gacha.unique_name}/orders/${order_id}/3ds2`;
};
